import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { API_URL } from 'constant';
import { loginState } from 'global-stores';
import { useNetwork } from 'hooks';

import { DashboardsAtom } from '../stores';
import { useDashboards } from '../stores/hooks';

export interface IRange {
	start: Date;
	end: Date;
	/** true for the range the app shell already prefetched on login */
	isDefault: boolean;
}

interface IDeckExtras {
	analytics: any;
	sessions: any[];
	envelopes: any[];
}

const EMPTY: IDeckExtras = { analytics: null, sessions: [], envelopes: [] };

const isoDay = (d: Date) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
		d.getDate()
	).padStart(2, '0')}`;

/**
 * One hook, one round of requests.
 *
 * What the old dashboard fired on mount: /user-locations?action=countries,
 * /onboarding-summary, /analytics?graph=true, /analytics (the same endpoint
 * again, its response written into a second atom), /user-locations?country=US.
 * Five, two of them identical, feeding a choropleth and three Highcharts
 * instances the deck does not draw. /onboarding-summary was the wasteful one:
 * routes-children already fetches it into DashboardsAtom at app mount, because
 * pipelines and billing read that atom too — so the dashboard was re-fetching a
 * response it already had in memory.
 *
 * The deck therefore issues TWO requests on first paint:
 *   /analytics        per-action status counts
 *   /complex-sessions the live queue
 * and re-fetches /onboarding-summary only when the range moves off the default
 * the shell already primed. /envelopes is deferred past first paint — one small
 * right-hand panel is never worth delaying the deck for.
 */
export const useOpsDeck = (range: IRange) => {
	const { business } = useRecoilValue(loginState) ?? {};
	const dashboards = useRecoilValue(DashboardsAtom);
	const { getDashboards } = useDashboards();

	// updateState:false — the deck reads returned payloads directly, so the
	// network hook's own data/status/isLoaded writes are pure re-render cost.
	const { get } = useNetwork({ updateState: false });

	const [extras, setExtras] = useState<IDeckExtras>(EMPTY);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState(false);

	// Range switching is a click target; a slow earlier response must not land
	// on top of a newer one. Cheaper than rebuilding useNetwork's axios config
	// to carry an AbortSignal — passing one there replaces the auth headers.
	const generation = useRef(0);

	const from = isoDay(range.start);
	const to = isoDay(range.end);
	const { isDefault } = range;

	const load = useCallback(async () => {
		if (!business) {
			// NO BUSINESS ON THE ACCOUNT IS NOT THE SAME AS "STILL LOGGING IN".
			//
			// This used to return with nothing else -- correct for the ordinary
			// first tick, where `business` is briefly empty while the app shell's
			// own login flow is still populating loginState, and this effect
			// re-fires (its `useCallback` depends on `business`) the moment it
			// does. But clients.api's own users.find() already documents a real,
			// non-transient case: a user record with no `business` at all, which
			// it fails SOFT for rather than 500ing (see the comment there). For
			// that account `business` never becomes truthy, so this effect never
			// runs again -- `loaded` stays false from the initial useState FOREVER,
			// and every tile on the deck (`loaded ? n(t.total) : '—'`) is stuck
			// showing "—" with the header pinned on "fetching…" no matter how long
			// the page sits open or how many times it is refreshed. That is what
			// made this look like the analytics/session requests were hanging --
			// they were never even sent.
			//
			// Settle the state instead of leaving it hanging: mark loaded so the
			// tiles stop reading as still-in-flight, and error so the header says
			// "summary unavailable" rather than "live" -- a business-less account
			// showing the SAME text as a real zero-activity business would hide
			// the very condition worth surfacing. If `business` was merely not
			// populated yet, the moment it is, `load`'s identity changes (it is a
			// dependency below) and the effect re-fires with the real data,
			// overwriting this.
			setExtras(EMPTY);
			setError(true);
			setLoaded(true);
			return;
		}
		const mine = ++generation.current;
		setLoaded(false);
		setError(false);

		// Only the shell's own range is already in the atom.
		if (!isDefault) getDashboards(range.start, range.end);

		const [analyticsResp, sessionResp] = await Promise.all([
			get(`/analytics?business=${business}&startDate=${from}&endDate=${to}`).catch(
				() => null
			),
			get(`${API_URL.COMPLEX_SESSION}?isDeleted=false`).catch(() => null),
		]);

		if (mine !== generation.current) return;

		setExtras(prev => ({
			...prev,
			analytics: analyticsResp?.data?.[0] ?? null,
			sessions: sessionResp?.data ?? [],
		}));
		setError(!analyticsResp && !sessionResp);
		setLoaded(true);

		const idle: (cb: () => void) => void =
			(window as any).requestIdleCallback ??
			((cb: () => void) => window.setTimeout(cb, 400));
		idle(async () => {
			const resp = await get(API_URL.ENVELOPE).catch(() => null);
			if (mine !== generation.current) return;
			setExtras(prev => ({ ...prev, envelopes: resp?.data ?? [] }));
		});
		// useNetwork's `get` identity churns every render; depending on it here
		// would re-fire the effect below forever.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [business, from, to, isDefault]);

	useEffect(() => {
		load();
	}, [load]);

	const summary = dashboards?.data?.summary ?? null;
	const chart = dashboards?.data?.chartData ?? null;

	return useMemo(
		() => ({
			...extras,
			summary,
			chart,
			loaded: loaded && dashboards.loaded,
			error: error || dashboards.error,
			reload: load,
		}),
		[extras, summary, chart, loaded, error, dashboards.loaded, dashboards.error, load]
	);
};
